import { dateQueryPreview, needsDatePreview } from "@/lib/date-query-preview";
import { cn } from "@/lib/utils";
import { ListTree } from "lucide-react";
import { COUNTED, DISTANCE_OPERATORS, formulaSummary, geoValueOf, geoValueSummary, joinValues, OPERATOR_LABELS, relatedPhrase, VALUELESS } from "./tree-utils";
import type { ConditionNode, FilterNode, FilterTree, GroupNode, RelatedNode } from "./types";
import { useFilterSchema, useResolvedPath } from "./use-filter-schema";

/** `inline`: inside a sentence, without the line of resolved dates under a relative one. */
function ConditionText({ model, node, inline }: { model: string; node: ConditionNode; inline?: boolean }) {
    const resolved = useResolvedPath(model, node.field);
    const label = resolved?.labels.join(" › ") ?? node.field;
    const options = resolved?.field?.options ?? [];
    const values = (node.value ?? []).map(value => {
        const key = String(value);
        return node.value_labels?.[key] ?? options.find(option => option.value === key)?.label ?? key;
    });
    const operator = node.operator ? OPERATOR_LABELS[node.operator] : "";
    if (node.formula != null) {
        return (
            <span>
                {label} {operator}
                <span className="font-medium text-gray-700"> {formulaSummary(node.formula_label || node.formula)}</span>
            </span>
        );
    }
    if (DISTANCE_OPERATORS.has(node.operator)) {
        return (
            <span>
                {label} {operator}
                <span className="font-medium text-gray-700"> {geoValueSummary(geoValueOf(node.value))}</span>
            </span>
        );
    }
    const shown = node.operator && !VALUELESS.has(node.operator) ? joinValues(values, node.operator === "between" || node.operator.startsWith("not_") ? "and" : "or") : "";
    const raw = (node.value ?? []).map(value => String(value));
    const type = resolved?.field?.type;
    const dates = node.operator && (type === "date" || type === "datetime") && needsDatePreview(raw) ? dateQueryPreview(node.operator, raw) : null;
    return (
        <span>
            {label} {operator}
            {shown && <span className="font-medium text-gray-700"> {shown}</span>}
            {dates && !inline && <span className="mt-0.5 block text-xs font-normal tabular-nums text-gray-400">{dates}</span>}
        </span>
    );
}

function RelatedText({ model, node }: { model: string; node: RelatedNode }) {
    const resolved = useResolvedPath(model, node.relation, true);
    const { schema } = useFilterSchema(resolved?.relation?.model);
    const phrase = relatedPhrase(node, resolved?.labels.join(" › ") ?? node.relation, schema);
    const count = node.filter?.children.length ?? 0;
    return (
        <span>
            <ListTree className="mr-1 inline h-3 w-3 align-[-2px]" />
            {phrase.quantifier.toLowerCase()}
            {COUNTED.has(node.quantifier) ? ` ${node.count ?? "…"}` : ""} {phrase.label}
            {count > 0 && (
                <span className="text-gray-500">
                    {" "}
                    ({count} condition{count > 1 ? "s" : ""})
                </span>
            )}
        </span>
    );
}

export function FilterNodeSummary({ model, node }: { model: string; node: FilterNode }) {
    if (node.type === "condition") return <ConditionText model={model} node={node} />;
    if (node.type === "related") return <RelatedText model={model} node={node} />;
    if (node.type === "drq") return <span>{node.label || "saved condition"}</span>;
    return (
        <span>
            ({node.children.length} condition{node.children.length > 1 ? "s" : ""} with {node.conjunction})
        </span>
    );
}

/** One line telling which filters a list applies, for the list header. */
export function FilterTreeSummary({ model, tree, className, onClick }: { model: string; tree: FilterTree; className?: string; onClick?: () => void }) {
    const children = tree.root.children;
    if (!children.length) return null;
    return (
        <button type="button" onClick={onClick} className={cn("min-w-0 truncate text-left text-xs text-gray-500 hover:text-gray-700", className)} title="Edit filters">
            Filters:{" "}
            {children.map((child, index) => (
                <span key={child.id}>
                    {index > 0 && <span className="font-medium"> {tree.root.conjunction} </span>}
                    <FilterNodeSummary model={model} node={child} />
                </span>
            ))}
        </button>
    );
}

/** A related block in words, its conditions included: « there is at least one opportunity where … ». */
function RelatedSentence({ model, node }: { model: string; node: RelatedNode }) {
    const resolved = useResolvedPath(model, node.relation, true);
    const target = resolved?.relation?.model;
    const { schema } = useFilterSchema(target);
    const phrase = relatedPhrase(node, resolved?.labels.join(" › ") ?? node.relation, schema);
    const children = node.filter?.children ?? [];
    return (
        <span>
            {phrase.quantifier.toLowerCase()}
            {COUNTED.has(node.quantifier) ? ` ${node.count ?? "…"}` : ""} <span className="font-medium text-gray-700">{phrase.label}</span>
            {target && children.length > 0 && (
                <>
                    {" "}
                    where (<GroupSentence model={target} group={node.filter as GroupNode} />)
                </>
            )}
        </span>
    );
}

function GroupSentence({ model, group }: { model: string; group: GroupNode }) {
    return (
        <>
            {group.children.map((child, index) => (
                <span key={child.id}>
                    {index > 0 && <span className="font-medium"> {group.conjunction} </span>}
                    {child.type === "related" ? (
                        <RelatedSentence model={model} node={child} />
                    ) : child.type === "group" ? (
                        <>
                            (<GroupSentence model={model} group={child} />)
                        </>
                    ) : child.type === "condition" ? (
                        <ConditionText model={model} node={child} inline />
                    ) : (
                        <FilterNodeSummary model={model} node={child} />
                    )}
                </span>
            ))}
        </>
    );
}

/**
 * The whole tree as one sentence, nested blocks spelled out: what a deep tree means at a glance.
 * Empty for a tree without related records, whose rows already read that way.
 */
export function FilterTreeSentence({ model, tree, className }: { model: string; tree: FilterTree; className?: string }) {
    if (!tree.root.children.some(child => child.type === "related")) return null;
    return (
        <p className={cn("rounded-sm border bg-gray-50 p-2 text-xs leading-5 text-gray-600", className)}>
            <span className="font-medium text-gray-700">In words: </span>
            <GroupSentence model={model} group={tree.root} />
        </p>
    );
}
